/**
 * GammaHeaderFooterSheet — 6-слотовые колонтитулы Gamma на мини-макете
 * слайда (владелец 2026-08-14 «мало колонтитулов»: раньше — максимум три
 * элемента, теперь каждый из 6 слотов кромки заполняется независимо, в т.ч.
 * НЕСКОЛЬКИМИ разными текстами: компания слева, дата справа, лого в центре).
 *
 * UI: кликаешь слот на макете → под макетом редактор слота (Пусто / Текст /
 * Номер / Логотип). Логотип-файл один на все logo-слоты (ключ S3 — свежий
 * presigned подписывает бэк при генерации), размер общий.
 *
 * Общий для десктопной панели (variant center) и мобильной шторки (bottom).
 * Черновик локальный (родители рендерят условно), коммит одним onSave на
 * любое закрытие. Модель/конвертеры — utils/gammaHf.
 */
import { useRef, useState } from 'react'
import Sheet from '../../../components/Sheet'
import { uploadFile } from '../../../api/images'
import {
	GAMMA_HF_POSITION_OPTIONS,
	GAMMA_HF_SIZE_OPTIONS,
	type GammaHfPosition,
	type GammaHfSlotContent,
} from '../utils/gammaOptions'
import type { GammaHfState } from '../utils/gammaHf'

interface GammaHeaderFooterSheetProps {
	open: boolean
	variant?: 'center' | 'bottom'
	value: GammaHfState
	onSave: (value: GammaHfState) => void
	onClose: () => void
}

const MAX_TEXT = 500
// Как у картинок презентаций (PresentationImagesSheet).
const MAX_LOGO_BYTES = 5 * 1024 * 1024

const TOP_SLOTS: GammaHfPosition[] = ['topLeft', 'topCenter', 'topRight']
const BOTTOM_SLOTS: GammaHfPosition[] = ['bottomLeft', 'bottomCenter', 'bottomRight']

function slotLabel(pos: GammaHfPosition): string {
	return GAMMA_HF_POSITION_OPTIONS.find((o) => o.value === pos)?.label ?? pos
}

export default function GammaHeaderFooterSheet({
	open,
	variant = 'center',
	value,
	onSave,
	onClose,
}: GammaHeaderFooterSheetProps) {
	const [draft, setDraft] = useState<GammaHfState>(value)
	const [selected, setSelected] = useState<GammaHfPosition | null>(null)
	const [uploading, setUploading] = useState(false)
	const [uploadError, setUploadError] = useState('')
	// Протухший превью-URL логотипа (presigned ~1ч): показываем «Лого»-плашку,
	// сам ключ файла рабочий — бэк подпишет свежий URL при генерации.
	const [brokenLogoUrl, setBrokenLogoUrl] = useState('')
	const fileInputRef = useRef<HTMLInputElement>(null)

	const patch = (p: Partial<GammaHfState>) => setDraft((d) => ({ ...d, ...p }))

	const setSlot = (pos: GammaHfPosition, content: GammaHfSlotContent | null) =>
		setDraft((d) => {
			const slots = { ...d.slots }
			if (content) slots[pos] = content
			else delete slots[pos]
			return { ...d, slots }
		})

	const pickType = (pos: GammaHfPosition, type: 'empty' | 'text' | 'number' | 'logo') => {
		if (type === 'empty') setSlot(pos, null)
		else if (type === 'text') {
			const cur = draft.slots[pos]
			setSlot(pos, { type: 'text', value: cur?.type === 'text' ? cur.value : '' })
		} else if (type === 'number') setSlot(pos, { type: 'number' })
		else {
			setSlot(pos, { type: 'logo' })
			// Логотипа ещё нет — сразу зовём выбор файла, чтобы слот не остался
			// пустышкой (без файла такой слот в запрос не уходит).
			if (!draft.logoKey) fileInputRef.current?.click()
		}
	}

	const handleLogoFile = async (file: File) => {
		if (!file.type.startsWith('image/')) {
			setUploadError('Выберите файл-картинку (PNG, JPG, SVG)')
			return
		}
		if (file.size > MAX_LOGO_BYTES) {
			setUploadError('Логотип больше 5 МБ — сожмите файл')
			return
		}
		setUploading(true)
		setUploadError('')
		try {
			const res = await uploadFile(file, 'uploads')
			setDraft((d) => ({ ...d, logoKey: res.key, logoUrl: res.url }))
		} catch (e) {
			setUploadError(e instanceof Error ? e.message : 'Не удалось загрузить логотип')
		} finally {
			setUploading(false)
		}
	}

	const done = () => {
		// Пустые тексты не сохраняем — слот очищается сам.
		const slots = { ...draft.slots }
		for (const [pos, content] of Object.entries(slots)) {
			if (content?.type === 'text') {
				const v = content.value.trim()
				if (v) slots[pos as GammaHfPosition] = { type: 'text', value: v }
				else delete slots[pos as GammaHfPosition]
			}
		}
		onSave({ ...draft, slots })
		onClose()
	}

	const selContent = selected ? draft.slots[selected] : undefined
	const selType: 'empty' | 'text' | 'number' | 'logo' = selContent?.type ?? 'empty'
	const hasAnySlot = Object.keys(draft.slots).length > 0

	const renderSlotButton = (pos: GammaHfPosition) => {
		const content = draft.slots[pos]
		const isSel = selected === pos
		let inner: React.ReactNode = <span className="gamma-hf-layout__plus">+</span>
		if (content?.type === 'text') {
			inner = (
				<span className="gamma-hf-layout__text" title={content.value || 'Текст'}>
					{content.value.trim() ? content.value : 'Текст…'}
				</span>
			)
		} else if (content?.type === 'number') {
			inner = <span className="gamma-hf-layout__num">1/N</span>
		} else if (content?.type === 'logo') {
			const logoOk = draft.logoUrl && draft.logoUrl !== brokenLogoUrl
			inner = logoOk ? (
				<img
					src={draft.logoUrl}
					alt="Логотип"
					className="gamma-hf-layout__logo"
					onError={() => setBrokenLogoUrl(draft.logoUrl)}
				/>
			) : (
				<span className="gamma-hf-layout__num">Лого</span>
			)
		}
		return (
			<button
				key={pos}
				type="button"
				className={
					'gamma-hf-layout__slot' +
					(content ? ' gamma-hf-layout__slot--filled' : '') +
					(isSel ? ' gamma-hf-layout__slot--selected' : '')
				}
				onClick={() => setSelected(isSel ? null : pos)}
				title={slotLabel(pos)}
				aria-label={slotLabel(pos)}
			>
				{inner}
			</button>
		)
	}

	return (
		<Sheet
			open={open}
			onClose={done}
			variant={variant}
			title="Колонтитулы"
			showHandle
			className="gamma-instructions-sheet gamma-hf-sheet"
		>
			<div className="gamma-instructions-sheet__body">
				<p className="gamma-instructions-sheet__hint">
					Шесть слотов по краям слайда — в каждом свой текст, номер слайда или
					логотип. Кликни слот на макете и выбери, что в нём показывать.
				</p>

				<div className="gamma-hf-layout" aria-label="Макет слайда">
					<div className="gamma-hf-layout__row">{TOP_SLOTS.map(renderSlotButton)}</div>
					<div className="gamma-hf-layout__mid" aria-hidden="true">
						<span className="gamma-hf-layout__ghost" />
						<span className="gamma-hf-layout__ghost gamma-hf-layout__ghost--short" />
					</div>
					<div className="gamma-hf-layout__row">{BOTTOM_SLOTS.map(renderSlotButton)}</div>
				</div>

				{selected ? (
					<div className="gamma-hf-sheet__group">
						<span className="gamma-hf-sheet__label">{slotLabel(selected)}</span>
						<div className="gamma-hf-sheet__chips">
							{([
								['empty', 'Пусто'],
								['text', 'Текст'],
								['number', 'Номер слайда'],
								['logo', 'Логотип'],
							] as const).map(([t, label]) => (
								<button
									key={t}
									type="button"
									className={'gamma-hf-sheet__chip' + (selType === t ? ' is-active' : '')}
									onClick={() => pickType(selected, t)}
								>
									{label}
								</button>
							))}
						</div>

						{selContent?.type === 'text' && (
							<input
								type="text"
								className="gamma-instructions-sheet__textarea gamma-hf-sheet__input"
								placeholder="Например: «ООО Ромашка» или «12.08.2026»"
								value={selContent.value}
								onChange={(e) =>
									setSlot(selected, {
										type: 'text',
										value: e.target.value.slice(0, MAX_TEXT),
									})
								}
								autoFocus
							/>
						)}

						{selContent?.type === 'logo' && (
							<>
								<div className="gamma-hf-sheet__logo-row">
									{draft.logoKey && draft.logoUrl && draft.logoUrl !== brokenLogoUrl && (
										<img
											src={draft.logoUrl}
											alt="Логотип"
											className="gamma-hf-sheet__logo-preview"
											onError={() => setBrokenLogoUrl(draft.logoUrl)}
										/>
									)}
									<button
										type="button"
										className="gamma-hf-sheet__chip"
										onClick={() => fileInputRef.current?.click()}
										disabled={uploading}
									>
										{uploading ? 'Загрузка…' : draft.logoKey ? 'Заменить файл' : 'Загрузить файл'}
									</button>
								</div>
								<div className="gamma-hf-sheet__chips">
									{GAMMA_HF_SIZE_OPTIONS.map((o) => (
										<button
											key={o.value}
											type="button"
											className={
												'gamma-hf-sheet__chip' +
												(draft.logoSize === o.value ? ' is-active' : '')
											}
											onClick={() => patch({ logoSize: o.value })}
										>
											{o.label}
										</button>
									))}
								</div>
							</>
						)}
					</div>
				) : (
					<p className="gamma-hf-sheet__pick-hint">
						Выбери слот на макете, чтобы настроить его.
					</p>
				)}

				{uploadError && <span className="gamma-hf-sheet__error">{uploadError}</span>}
				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					hidden
					onChange={(e) => {
						const f = e.target.files?.[0]
						if (f) void handleLogoFile(f)
						e.target.value = ''
					}}
				/>

				<div className="gamma-hf-sheet__group">
					<span className="gamma-hf-sheet__label">Не показывать</span>
					<div className="gamma-hf-sheet__chips">
						<button
							type="button"
							className={'gamma-hf-sheet__chip' + (draft.hideFirst ? ' is-active' : '')}
							onClick={() => patch({ hideFirst: !draft.hideFirst })}
							disabled={!hasAnySlot}
						>
							На первом слайде
						</button>
						<button
							type="button"
							className={'gamma-hf-sheet__chip' + (draft.hideLast ? ' is-active' : '')}
							onClick={() => patch({ hideLast: !draft.hideLast })}
							disabled={!hasAnySlot}
						>
							На последнем
						</button>
					</div>
				</div>

				<div className="gamma-instructions-sheet__footer">
					<span className="gamma-instructions-sheet__counter">
						{Object.keys(draft.slots).length} / 6 слотов
					</span>
					<button type="button" className="gamma-instructions-sheet__btn" onClick={done}>
						Готово
					</button>
				</div>
			</div>
		</Sheet>
	)
}
